import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Alert,
  TextField,
  IconButton,
} from "@mui/material";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import {
  Campaign,
  CampaignState,
  GroupCategory,
  Team,
  SubmissionType,
} from "../../types";
import { MapList, MappedItem } from "../MapList";
import { GroupCategorySelector } from "../GroupCategory/Selector";
import { SubmissionTypeSelector } from "../SubmissionType/Selector";
import { SimpleDebouncedMDXEditor } from "../SimpleDebouncedMDXEditor";
import { FileAttachment } from "../FileUpload/FileAttachment";
import {
  GET_ASSIGNMENT_PDF_UPLOAD_URL,
  CONFIRM_ASSIGNMENT_PDF_UPLOAD,
  DELETE_ASSIGNMENT_PDF_ATTACHMENT,
} from "../../Queries";
import dayjs, { Dayjs } from "dayjs";

interface Props {
  campaign: Campaign;
  categories: GroupCategory[];
  teams: Team[];
  onChange: (campaign: Campaign, activityLabel: string) => void;
  onSetGroupCategory: (
    campaign: Campaign,
    groupcategory: GroupCategory | undefined,
  ) => void;
}

export const AssignmentDetails: React.FC<Props> = ({
  campaign,
  categories,
  teams,
  onChange,
  onSetGroupCategory,
}) => {
  const [t] = useTranslation();
  const [sendDateOpen, setSendDateOpen] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [closeDateOpen, setCloseDateOpen] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  const [getUploadUrl] = useMutation(GET_ASSIGNMENT_PDF_UPLOAD_URL, {
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message || "Failed to get upload URL");
    },
  });

  const [confirmUpload] = useMutation(CONFIRM_ASSIGNMENT_PDF_UPLOAD, {
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message || "Failed to confirm upload");
    },
  });

  const [deleteAttachment] = useMutation(DELETE_ASSIGNMENT_PDF_ATTACHMENT, {
    onError: (error) => {
      setFileUploading(false);
      setFileUploadError(error.message || "Failed to delete attachment");
    },
  });

  // Handle description changes - update through campaign
  const handleDescriptionChange = useCallback(
    (description: string) => {
      if (campaign.assignment) {
        onChange(
          {
            ...campaign,
            assignment: {
              ...campaign.assignment,
              description,
            },
          },
          "Updated assignment description",
        );
      }
    },
    [campaign.id, campaign.assignment?.id, onChange],
  );

  if (!campaign.assignment) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            Assignment data not available. Please refresh the page.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Handle submission type changes - update through campaign
  function handleSubmissionTypeChange(submissionType: SubmissionType) {
    if (campaign.assignment) {
      const isLive = submissionType === SubmissionType.LivePresentation;
      onChange(
        {
          ...campaign,
          assignment: {
            ...campaign.assignment,
            submissionType,
            // If switching to Live Presentation, clear assignment dates
            assignmentSendDate: isLive
              ? null
              : campaign.assignment.assignmentSendDate,
            assignmentDueDate: isLive
              ? null
              : campaign.assignment.assignmentDueDate,
            assignmentCloseDate: isLive
              ? null
              : campaign.assignment.assignmentCloseDate,
          },
        },
        "Updated assignment submission type",
      );
    }
  }

  // Handle date changes - update through campaign
  function handleAssignmentSendDateChange(date: Dayjs | null) {
    if (campaign.assignment) {
      onChange(
        {
          ...campaign,
          assignment: {
            ...campaign.assignment,
            assignmentSendDate: date ? date.toISOString() : null,
          },
        },
        "Updated assignment send date",
      );
    }
  }

  function handleAssignmentDueDateChange(date: Dayjs | null) {
    if (campaign.assignment) {
      onChange(
        {
          ...campaign,
          assignment: {
            ...campaign.assignment,
            assignmentDueDate: date ? date.toISOString() : null,
          },
        },
        "Updated assignment due date",
      );
    }
  }

  function handleAssignmentCloseDateChange(date: Dayjs | null) {
    if (campaign.assignment) {
      onChange(
        {
          ...campaign,
          assignment: {
            ...campaign.assignment,
            assignmentCloseDate: date ? date.toISOString() : null,
          },
        },
        "Updated assignment close date",
      );
    }
  }

  const handleGetPdfUploadUrl = async (filename: string, fileSize: number) => {
    if (!campaign.assignment) throw new Error("No assignment to upload to");
    setFileUploading(true);
    setFileUploadError(null);
    const res = await getUploadUrl({
      variables: {
        assignmentId: campaign.assignment.id,
        filename,
        fileSize,
      },
    });
    setFileUploading(false);
    const data = res.data?.getAssignmentPdfUploadUrl;
    if (!data || !data.uploadUrl) {
      throw new Error("Unable to get upload URL");
    }
    return data;
  };

  const handleConfirmPdfUpload = async (
    fileUrl: string,
    originalFilename: string,
  ) => {
    if (!campaign.assignment)
      throw new Error("No assignment to confirm upload for");
    setFileUploading(true);
    setFileUploadError(null);
    const res = await confirmUpload({
      variables: {
        assignmentId: campaign.assignment.id,
        fileUrl,
        originalFilename,
      },
    });
    setFileUploading(false);
    const updated = res.data?.confirmAssignmentPdfUpload;
    if (updated && updated.id) {
      onChange(
        {
          ...campaign,
          assignment: {
            ...campaign.assignment,
            pdfAttachment: updated.pdfAttachment,
            pdfAttachmentUrl: updated.pdfAttachmentUrl,
          },
        },
        "Updated assignment PDF",
      );
    }
  };

  const handleDeletePdf = async () => {
    if (!campaign.assignment) throw new Error("No assignment to delete from");
    setFileUploading(true);
    setFileUploadError(null);
    const res = await deleteAttachment({
      variables: {
        assignmentId: campaign.assignment.id,
      },
    });
    setFileUploading(false);
    const updated = res.data?.deleteAssignmentPdfAttachment;
    if (updated && updated.id) {
      onChange(
        {
          ...campaign,
          assignment: {
            ...campaign.assignment,
            pdfAttachment: null,
            pdfAttachmentUrl: null,
          },
        },
        "Removed assignment PDF",
      );
    }
  };

  function handleSetGroupCategoryClick(
    groupcategory: GroupCategory | undefined,
  ) {
    onSetGroupCategory(campaign, groupcategory);
  }

  // Content for all states - uses campaign data and onChange
  function AssignmentContent() {
    const canEdit = [
      CampaignState.Draft,
      CampaignState.AssignmentScheduled,
      CampaignState.Scheduled,
    ].includes(campaign.state);

    const isLivePresentation =
      campaign.assignment?.submissionType === SubmissionType.LivePresentation;

    const items: MappedItem[] = [
      {
        label: "Submission Format",
        value: canEdit ? (
          <SubmissionTypeSelector
            selected={campaign.assignment?.submissionType}
            onChange={handleSubmissionTypeChange}
          />
        ) : campaign.assignment?.submissionType === SubmissionType.Document ? (
          "Document"
        ) : campaign.assignment?.submissionType === SubmissionType.Video ? (
          "Video"
        ) : campaign.assignment?.submissionType === SubmissionType.Text ? (
          "Text-only submission"
        ) : campaign.assignment?.submissionType ===
          SubmissionType.LivePresentation ? (
          "Live presentation"
        ) : (
          "Document"
        ),
      },
      {
        label: "Description",
        value: (
          <SimpleDebouncedMDXEditor
            value={campaign.assignment?.description || ""}
            onChange={handleDescriptionChange}
            placeholder="Enter assignment description..."
            readOnly={!canEdit}
            minHeight="150px"
          />
        ),
      },
      // File Attachment row is conditionally included below
    ];

    const dateItems: MappedItem[] = [];
    if (!isLivePresentation) {
      dateItems.push({
        label: "Send Date",
        value: canEdit ? (
          <DesktopDateTimePicker
            open={sendDateOpen}
            onOpen={() => setSendDateOpen(true)}
            onClose={() => setSendDateOpen(false)}
            label="Assignment Send Date"
            value={
              campaign.assignment?.assignmentSendDate
                ? dayjs(campaign.assignment.assignmentSendDate)
                : null
            }
            onAccept={(date) => {
              handleAssignmentSendDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setSendDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleAssignmentSendDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ) : campaign.assignment?.assignmentSendDate ? (
          dayjs(campaign.assignment.assignmentSendDate).format(
            "M/DD/YYYY h:mm A",
          )
        ) : (
          "Not set"
        ),
      });
      dateItems.push({
        label: "Due Date",
        value: canEdit ? (
          <DesktopDateTimePicker
            open={dueDateOpen}
            onOpen={() => setDueDateOpen(true)}
            onClose={() => setDueDateOpen(false)}
            label="Assignment Due Date"
            value={
              campaign.assignment?.assignmentDueDate
                ? dayjs(campaign.assignment.assignmentDueDate)
                : null
            }
            onAccept={(date) => {
              handleAssignmentDueDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setDueDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleAssignmentDueDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ) : campaign.assignment?.assignmentDueDate ? (
          dayjs(campaign.assignment.assignmentDueDate).format(
            "M/DD/YYYY h:mm A",
          )
        ) : (
          "Not set"
        ),
      });
      dateItems.push({
        label: "Close Date",
        value: canEdit ? (
          <DesktopDateTimePicker
            open={closeDateOpen}
            onOpen={() => setCloseDateOpen(true)}
            onClose={() => setCloseDateOpen(false)}
            label="Assignment Close Date"
            value={
              campaign.assignment?.assignmentCloseDate
                ? dayjs(campaign.assignment.assignmentCloseDate)
                : null
            }
            onAccept={(date) => {
              handleAssignmentCloseDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setCloseDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleAssignmentCloseDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ) : campaign.assignment?.assignmentCloseDate ? (
          dayjs(campaign.assignment.assignmentCloseDate).format(
            "M/DD/YYYY h:mm A",
          )
        ) : (
          "Not set"
        ),
      });
    }

    // Conditionally include the File Attachment row.
    // Show when campaign is editable or when an attachment already exists.
    const showAssignmentPdf =
      canEdit || Boolean(campaign.assignment?.pdfAttachmentUrl);

    const pdfItems = showAssignmentPdf
      ? [
          {
            label: "File Attachment",
            value: (
              <FileAttachment
                onGetUploadUrl={handleGetPdfUploadUrl}
                onConfirmUpload={handleConfirmPdfUpload}
                onUploadError={(message) => {
                  setFileUploading(false);
                  setFileUploadError(message);
                }}
                onDelete={canEdit ? handleDeletePdf : undefined}
                disabled={!canEdit}
                acceptedFileTypes=".pdf"
                maxFileSize={50}
                uploading={fileUploading}
                uploadError={fileUploadError}
                existingFileUrl={campaign.assignment?.pdfAttachmentUrl || null}
                existingFileName={
                  campaign.assignment?.pdfAttachment || undefined
                }
                autoUpload={true}
              />
            ),
          } as const,
        ]
      : [];

    // Add Group Category and Teams Count as final fields for Assignment campaigns
    const additionalItems = [
      {
        label: "Group Category",
        labelId: "category-select-label",
        value: (
          <GroupCategorySelector
            categories={categories}
            selected={campaign.groupcategory}
            onChange={(groupcategory) =>
              handleSetGroupCategoryClick(groupcategory)
            }
          />
        ),
      },
      {
        label: t("assignments.teamCount"),
        value: teams.length,
      },
    ];

    return (
      <MapList
        items={[...items, ...dateItems, ...pdfItems, ...additionalItems]}
      />
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <AssignmentContent />
      </CardContent>
    </Card>
  );
};
